import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmailProductData, OrderHistory, TaxCalculationMethod } from '../../stores/appOrderHistoryStore';
import { Platform } from '../../types/Platforms';
import { getProductQuantity, platformsShippingIncluded } from '../../utils/platformUtils';
import { cn } from '../../lib/utils';
import { extraSmallTextSize, lightBlueBG, roundedLG, semiBoldText, textBlue } from '../../styling/stylingHelper';


/**
 * Get the display price for a product based on VAT toggle
 */
export function getProductPrice(product: EmailProductData, showExVat: boolean): number {
  if (showExVat) {
    return product.price;
  }
  return product.priceWithTaxes ?? product.price;
}

/**
 * Get the display total for an order based on VAT toggle
 */
export function getOrderTotal(order: OrderHistory, showExVat: boolean): number {
  if (showExVat) {
    return order.total;
  }
  return order.totalWithTaxes ?? order.total;
}

export function getOrderVatAmount(platform: Platform, totalWithTaxes: number, total: number, shipping: number): number {
  var taxAmount = (totalWithTaxes ?? 0) - (total ?? 0) - (shipping ?? 0);
  // If platform is in the platformsShippingIncluded list, don't subtract shipping
  if (platformsShippingIncluded.includes(platform)) {
    taxAmount = (totalWithTaxes ?? 0) - (total ?? 0);
  }
  if (taxAmount < 0) {
    if (total > 0) {
      return 0;
    }
  }
  return taxAmount
}

/**
 * VAT charged on the line items, summed from their own rates, with the matching ex-VAT base.
 * Returns undefined when any line lacks `priceWithTaxes` (not every platform stores it) so callers
 * can fall back. Multiplies by `getProductQuantity` — the same price × quantity convention every
 * other financial calculation uses (per-kg platforms multiply by rawQuantity).
 */
function getVatFromLines(
  products: EmailProductData[] | undefined,
  platform: Platform
): { vat: number; base: number } | undefined {
  if (!products || products.length === 0) return undefined;

  let vat = 0;
  let base = 0;
  for (const product of products) {
    if (product.priceWithTaxes === undefined || product.price === undefined) return undefined;
    const quantity = getProductQuantity(product, platform);
    vat += (product.priceWithTaxes - product.price) * quantity;
    base += product.price * quantity;
  }

  return base > 0 ? { vat, base } : undefined;
}

/**
 * Calculate the VAT rate based on order totals, respecting platform-specific rules
 * Returns the VAT rate as a decimal (e.g., 0.21 for 21%)
 */
export function getOrderVatRate(
  platform: Platform,
  totalWithTaxes: number,
  total: number,
  shipping: number,
  products?: EmailProductData[]
): number | undefined {
  // Preferred: the rate the supplier actually charged on the goods, straight from the lines. This
  // needs NO assumption about the shipping surcharge — suppliers differ on whether they tax it, so
  // deriving the base from `total` and `shipping` cannot be right for all of them.
  const fromLines = getVatFromLines(products, platform);
  if (fromLines) {
    const lineRate = fromLines.vat / fromLines.base;
    return lineRate >= 0 ? lineRate : undefined;
  }

  // Fallback — lines carry no tax data. `total` IS the taxable base here: for shipping-included
  // platforms it already contains the shipping, for the others shipping sits in its own column and
  // is excluded from both the VAT amount and the base.
  const vatAmount = getOrderVatAmount(platform, totalWithTaxes, total, shipping);
  if (total <= 0) {
    return undefined;
  }

  const rate = vatAmount / total;

  // Only return valid rates (>= 0)
  return rate >= 0 ? rate : undefined;
}

/**
 * Format tax rate as percentage (rounded to integer)
 */
export function formatTaxRate(taxRate?: number): string {
  if (taxRate === undefined) return '';
  return `${Math.round(taxRate * 100)}%`;
}

/**
 * Format price to max 2 decimal places
 */
export function formatPrice(price: number): string {
  return price.toFixed(2);
}

/**
 * Check if tax data is estimated (not provided from invoice)
 */
export function isTaxEstimated(taxCalculationMethod?: TaxCalculationMethod): boolean {
  return taxCalculationMethod !== 'provided';
}

/**
 * Component to display tax rate with optional warning icon for estimated values
 */
export const TaxRateDisplay: React.FC<{
  taxRate?: number;
  taxCalculationMethod?: TaxCalculationMethod;
  className?: string;
}> = ({ taxRate, taxCalculationMethod, className = '' }) => {
  const { t } = useTranslation();

  if (taxRate === undefined) return null;

  const isEstimated = isTaxEstimated(taxCalculationMethod);
  const rateText = formatTaxRate(taxRate);

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      ({rateText}
      {isEstimated && (
        <AlertTriangle
          className="w-3 h-3 text-orange-500 dark:text-darkOrangeColor-dark"
          data-tooltip={t('orderHistory.estimatedTaxTooltip')}
        />
      )})
    </span>
  );
};

/**
 * The VAT rates charged on one order, as a compact badge ("21%", or "21% / 9%" when the invoice
 * mixes rates). Feed it the per-LINE rates (`product.taxRate`, decimals like 0.21) and it does the
 * filtering, de-duplication and ordering, so every surface showing rates renders the same thing.
 * Renders nothing when no usable rate is known. Carries the same estimated-rate warning as
 * {@link TaxRateDisplay} — the rate was derived by us, not printed on the invoice.
 */
export const VatRateBadges: React.FC<{
  rates: (number | undefined)[];
  taxCalculationMethod?: TaxCalculationMethod;
  className?: string;
}> = ({ rates, taxCalculationMethod, className = '' }) => {
  const { t } = useTranslation();

  // De-duplicate on the DISPLAYED percentage: 0.2100 and 0.2099 are one badge, not two.
  const seenPercentages = new Set<number>();
  const distinctRates: number[] = [];
  for (const rate of rates) {
    if (rate === undefined || rate === null || !isFinite(rate) || rate < 0) continue;
    const percentage = Math.round(rate * 100);
    if (seenPercentages.has(percentage)) continue;
    seenPercentages.add(percentage);
    distinctRates.push(rate);
  }
  if (distinctRates.length === 0) return null;
  distinctRates.sort((a, b) => b - a);

  return (
    <span className={`inline-flex items-center justify-center gap-1 ${className}`}>
      <span className={cn(textBlue, extraSmallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap')}>
        {distinctRates.map(formatTaxRate).join(' / ')}
      </span>
      {isTaxEstimated(taxCalculationMethod) && (
        <AlertTriangle
          className="w-3 h-3 flex-shrink-0 text-orange-500 dark:text-darkOrangeColor-dark"
          data-tooltip={t('orderHistory.estimatedTaxTooltip')}
        />
      )}
    </span>
  );
};
